import { Component, OnInit } from '@angular/core';
import { DialogManagerService } from '@src/services';
import { vaultActions } from '@src/store/reducers/vaultReducer';
import { Store } from '@src/store';
import { userActions } from '@src/store/reducers/userReducer';
import { cloneDeep } from 'lodash';
import { agentActions } from '@src/store/reducers/agentReducer';
import { ClusterDialogComponent } from '@app/shared/components/dialogs/cluster-dialog/cluster-dialog.component';

export interface IVault {
    ID: number;
    Name: string;
    VaultTypeId: number;
    VaultType: string;
    isNew?: boolean;
    Configuration: any;
}

@Component({
    selector: 'vault-management',
    template: require('./vault-management.component.html'),
    styles: [require('./vault-management.component.scss')]
})
export class VaultManagementComponent implements OnInit {
    public vaults: IVault[];
    public vault: IVault;
    private testPreventAction = false;
    private tempVault: any;
    private indexInList: any = 0;
    private userId: number;
    private isConnection: boolean = false;
    private isCredential: boolean = false;
    private clusterId: number;

    constructor(private store: Store, private dialogManagerService: DialogManagerService) {
    }

    public async ngOnInit() {
        this.vaults = await this.store.dispatch({ type: vaultActions.GET_VAULTS });
        this.vault = this.vaults ? this.vaults[0] : null;
        this.tempVault = this.vaults ? this.vaults[0] : null;
        const userDetails = await this.store.dispatch({ type: userActions.GET_CURRENT_USER_DETAILS });
        this.userId = userDetails.data.id;
    }

    public getVault(selectedVault: any, indexInList: any) {
        if (this.testPreventAction) {
            return;
        }
        this.indexInList = indexInList;
        this.vault = selectedVault;
    }

    public addVault() {

        this.vault = {
            ID: -1, Name: 'New Vault', VaultTypeId: 1,
            VaultType: 'AWS', isNew: true, Configuration: {}
        };
        this.tempVault = {
            ID: -1, Name: 'New Vault', VaultTypeId: 1,
            VaultType: 'AWS', isNew: true, Configuration: {}
        };
        if (!this.vaults) {
            this.vaults = [];
        }
        this.vaults = [this.vault, ...this.vaults];
    }

    public async testVaultConnection(vault) {
        this.isConnection = false;
        this.isCredential = false;
        this.clusterId = -1;
        await this.checkVaultInCluster(vault);
    }

    private async showAdditionalOptionDialog(vault) {
        const htmlConnTemplate = await this.getHtmlTemplate(vault);

        const testDataObject = {
            clusterId: -1,
            configuration: vault,
            credentials: { ID: vault.ID, UserId: this.userId, Credentials: {} },
            connection: { VaultId: vault.ID, Connection: {} }
        };
        this.testPreventAction = true;
        this.dialogManagerService.showVaultDialog(
            htmlConnTemplate,
            {
                title: 'Vault Details',
                buttons: [
                    {
                        text: 'Test Connection', onClick: (instance, onClose) => {
                            onClose(instance.data);
                        }
                    },
                    {
                        text: 'Cancel', onClick: (_, onClose) => {
                            onClose();
                        }
                    }
                ],
                setData: (instance) => {
                    instance.data = {
                        credentials: { },
                        connection: { }
                    };
                }
            },
            async (data) => {
                testDataObject.connection.Connection = !this.isConnection ? null : data.connection;
                testDataObject.credentials.Credentials = !this.isCredential ? null : data.credentials;
                testDataObject.clusterId = this.clusterId;

                await this.store.dispatch({ type: vaultActions.TEST_VAULT_CONNECTION,
                    payload: { testData: testDataObject }
                });
                this.testPreventAction = false;
            }
        );
    }

    private async getHtmlTemplate(vault): Promise<string> {
        return new Promise(async (resolve) => {
            let htmlConnTemplate = '';
            const credentialTemplate = await this.store.dispatch({
                type: vaultActions.POST_VAULT_CREDENTIALS, payload: { vault }
            });
            const connectionTemplate = await this.store.dispatch({
                type: vaultActions.POST_VAULT_CONNECTION, payload: { vault }
            });
            if (connectionTemplate) {
                this.isConnection = true;
                htmlConnTemplate = connectionTemplate;
            }
            if (credentialTemplate) {
                this.isCredential = true;
                htmlConnTemplate = htmlConnTemplate + credentialTemplate;
            }
            resolve(htmlConnTemplate);
        });
    }

    private async checkVaultInCluster(vault) {
        const vaultInCluster = await this.store.dispatch({
            type: agentActions.CHECK_VAULT_IN_CLUSTER, payload: { vault }
        });
        if (vaultInCluster) {
            await this.showClusterDialog(vault);
        } else {
            await this.showAdditionalOptionDialog(vault);
        }
    }

    private async showClusterDialog(vault) {
        const clusters = await this.store.dispatch({ type: agentActions.GET_CLUSTER_WITH_AGENT });
        const onConfirm = (data) => {
            this.clusterId = data.selected.ID;
            this.showAdditionalOptionDialog(vault);

        };
        const dialogData = {
            clusters: clusters.filter((cluster: any) => cluster.Agents.length > 0),
            title: 'Select Cluster',
            fieldName: 'Cluster'
        };
        this.dialogManagerService.showDialog(dialogData, onConfirm, ClusterDialogComponent, 'cluster-dialog');
    }

    public isTest() {
        return this.vault && this.store.getState().vaults.test;
    }

    public onCancel() {
        this.vault = cloneDeep(this.tempVault);
    }

    public delete() {
        const onConfirm = async () => {
            if (this.vault.isNew) {
                this.deleteVaultFromList();
                return;
            }
            const response = await this.store.dispatch({
                type: vaultActions.DELETE_VAULT_CONFIGURATION,
                payload: { vaultId: this.vault.ID }
            });
            if (response.status === 200) {
                this.deleteVaultFromList();
            }
        };
        const dialogData = { title: 'Delete Vault', action: 'delete', objectType: 'vault', item: this.vault.Name };
        this.dialogManagerService.showConfirmationDialog(dialogData, onConfirm);
    }

    private deleteVaultFromList(): void {
        this.vaults.splice(this.indexInList, 1);
        const index = this.vaults.length >= this.indexInList + 1 ? this.indexInList : this.indexInList - 1;
        this.vault = this.vaults ? this.vaults[index] : null;
        this.tempVault = this.vaults ? this.vaults[index] : null;
        this.vaults = [...this.vaults];
    }

    public onSave(vaultId): void {
        if (vaultId) {
            this.vault.ID = vaultId;
            this.tempVault.ID = vaultId;
        }
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/authorized/vault-management/vault-management.component.ts